/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Component } from "react";
import { connect } from "react-redux";
import { reduxForm, Field, change } from "redux-form";
import actionCreators from "../../../../../store/OfficeHours/Reports/actionCreators";
import appointmentsActionCreators from "../../../../../store/OfficeHours/Appointments/actionCreators";
import { default as availabilityActionCreators } from "../../../../../store/OfficeHours/Availabilities/actionCreators";
import {
    RenderSelectField,
    RenderDatepicker,
    RenderAutocomplete,
} from "../../../../../common/components/FormFields";
import { withStyles, Button } from "@material-ui/core";
import formsCss from "../../../../../common/styles/forms.css";
import globalsCss from "../../../../../common/styles/globals.css";
import { processSchoolYears } from "../../helpers/schoolYear";
import { validateRequiredFields } from "../../../../../common/services/formHelpers";
import autoCompleteActionCreators from "../../../../../store/AutoComplete/actionCreators";
import { Subject } from "rxjs";
import { distinctUntilChanged, debounceTime } from "rxjs/operators";
import { rawUpdateListOnFilterChange } from "../../../../../common/services/FilterList";

class TeamAvailabilityForm extends Component {
    filter$ = new Subject();

    componentDidMount() {
        const {
            schoolYears,
            gettOfficeHoursSchoolYearsAndTerms,
            getOfficeHoursTeams,
            clearErrorResponse,
            teams,
            getMyOfficeHoursTeams,
            userId,
        } = this.props;

        clearErrorResponse();

        if (schoolYears === null) {
            gettOfficeHoursSchoolYearsAndTerms();
        }

        if (!teams) getMyOfficeHoursTeams(userId);

        this.subscription = this.filter$
            .pipe(debounceTime(500), distinctUntilChanged())
            .subscribe(filter => {
                rawUpdateListOnFilterChange({
                    getFn: getOfficeHoursTeams,
                    filter: { filter, type: "admin" },
                });
            });

        this.props.initialize(this.props.initialValues);
    }

    componentWillUnmount() {
        this.props.setOfficeHoursTeams([]);
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    handleModuleAutoComplete = filter => {
        this.filter$.next(filter);
    };

    clearSchoolYearSelect = () => {
        this.props.updateForm("schoolyear", null);
    };

    handleChangeSchoolYear = (e, value) => {
        const dates = value.split("#");
        this.props.updateForm("from", dates[0]);
        this.props.updateForm("to", dates[1]);
    };

    handleIncludeAll = () => {
        this.props.updateForm("teams", this.props.teams);
    };

    onSubmit = values => {
        const teams = values.teams.map(a => a.id);
        this.props.createTeamAvailabilityReport({
            from: values.from,
            to: values.to,
            "teams[]": teams,
        });
    };

    render() {
        const {
            handleSubmit,
            schoolYears,
            officeHoursTeams,
            errorRes: { error, errors },
        } = this.props;

        if (schoolYears === null) return null;

        return (
            <form onSubmit={handleSubmit(this.onSubmit)}>
                <div className="row">
                    <div className="col-xs-12">
                        <label css={formsCss.labelBig}>Select Teams</label>
                    </div>
                    <div className="col-sm-6 col-xs-12">
                        <Field
                            name="teams"
                            handleInput={this.handleModuleAutoComplete}
                            dataSource={officeHoursTeams}
                            component={RenderAutocomplete}
                            floatingLabelText="Select Team"
                            labelCallback={obj => obj.name}
                            fullWidth
                            noAvatar
                        />
                        <div css={globalsCss.helpers.mt15}>
                            <Button onClick={this.handleIncludeAll}>Add All</Button>
                        </div>
                    </div>
                </div>
                <hr />
                <div css={globalsCss.helpers.mt20}>
                    <h4>Time period</h4>
                </div>
                <div className="row">
                    <div className="col-sm-4 col-xs-12">
                        <div css={formsCss.labelBig}>Select year, term ... </div>
                        <Field
                            name="schoolyear"
                            component={RenderSelectField}
                            label="Select value"
                            errorRes={errors.schoolyear}
                            options={processSchoolYears(schoolYears)}
                            onChange={this.handleChangeSchoolYear}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <div css={formsCss.labelBig}>...or time period</div>
                    </div>
                    <div className="col-sm-4 col-xs-12">
                        <Field
                            name="from"
                            errorRes={errors.from}
                            component={RenderDatepicker}
                            format={null}
                            hintText="Enter Date"
                            fullWidth
                            css={formsCss.field}
                            onChange={this.clearSchoolYearSelect}
                        />
                    </div>
                    <div className="col-sm-4 col-xs-12">
                        <Field
                            name="to"
                            errorRes={errors.to}
                            component={RenderDatepicker}
                            format={null}
                            hintText="Enter Date"
                            fullWidth
                            css={formsCss.field}
                        />
                    </div>
                </div>
                {error && (
                    <div css={[formsCss.genericError, formsCss.genericErrorMarginTop]}>{error}</div>
                )}
                <div css={formsCss.actions}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        css={formsCss.btnSubmit}
                    >
                        Generate report
                    </Button>
                </div>
            </form>
        );
    }
}

const FORM_NAME = "OfficeHoursAcademicAvailability";

const validate = values => {
    const requiredFields = {
        from: "Required",
        to: "Required",
        teams: "Required",
    };

    if (!!values.from && !!values.to && values.from > values.to)
        return { to: "End date must be greater than start date" };

    return validateRequiredFields(requiredFields, values);
};

const form = reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    validate,
})(withStyles(formsCss)(TeamAvailabilityForm));

const mapStateToProps = ({
    OfficeHoursReport: { errorRes },
    OfficeHoursAppointments: { schoolYears },
    AutoComplete: { officeHoursTeams },
    OfficeHoursAvailabilities: { teams },
    Auth: { id },
}) => {
    return {
        schoolYears,
        officeHoursTeams,
        errorRes,
        teams: teams ? teams.filter(team => team.userType === "admin") : [],
        userId: id,
    };
};

const dispatchToProps = {
    updateForm: (key, newValue) => change(FORM_NAME, key, newValue),
    gettOfficeHoursSchoolYearsAndTerms:
        appointmentsActionCreators.gettOfficeHoursSchoolYearsAndTerms.create,
    getOfficeHoursTeams: autoCompleteActionCreators.getOfficeHoursTeams.create,
    setOfficeHoursTeams: autoCompleteActionCreators.setOfficeHoursTeams.create,
    createTeamAvailabilityReport: actionCreators.createTeamAvailabilityReport.create,
    clearErrorResponse: actionCreators.clearErrorResponse.create,
    getMyOfficeHoursTeams: availabilityActionCreators.getMyOfficeHoursTeams.create,
};

export default connect(mapStateToProps, dispatchToProps)(form);
